import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Check } from 'lucide-react';
import ReadEditorContent from '../../utils/readEditorContent';
import { generatePdfFromDocument } from '../../utils/generatePdfFromDocument';
import { downloadSealedSignedPdf, downloadCertificateJson } from '../../utils/downloadSealedSignedPdf';
import { editorJsonToHtml } from '../../utils/editorJsonToHtml';
import SignatureReceiptFlow from './SignatureReceiptFlow';
import {
  applyPaymentButtonReadOnlyState,
  formatPaymentCompletedDisplay,
} from '../../utils/paymentButtonReadOnlyState';
import toast from 'react-hot-toast';

export default function SharedSignatureReceiptView() {
  const { documentId, submissionId } = useParams();
  const [searchParams] = useSearchParams();
  const receiptToken = searchParams.get('receiptToken');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sealedCertificate, setSealedCertificate] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!documentId || !submissionId || !receiptToken) {
      setError('Invalid receipt link.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const functions = getFunctions();
        const getReceipt = httpsCallable(functions, 'getSharedSignatureReceipt');
        const res = await getReceipt({ documentId, submissionId, receiptToken });
        if (cancelled) return;
        const payload = res.data || {};
        setReceiptData(payload);
        if (payload.certificateOfCompletion) setSealedCertificate(payload.certificateOfCompletion);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load signed document.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [documentId, submissionId, receiptToken]);

  const paymentComplete = !!receiptData?.paymentCompletedAt;
  const paymentEnabled = !!receiptData?.paymentEnabled;
  const paymentAmountCents = receiptData?.paymentAmountCents ?? null;
  const payNowUrl = receiptData?.payNowUrl || null;
  const paidLinkIds = receiptData?.paidLinkIds || null;

  const handlePayClick = useCallback((info) => {
    if (!payNowUrl) {
      toast.error('Payment link is not available.');
      return;
    }
    const paymentLinkId = info?.paymentLinkId || null;
    const url = paymentLinkId
      ? `${payNowUrl}${payNowUrl.includes('?') ? '&' : '?'}paymentLinkId=${encodeURIComponent(paymentLinkId)}`
      : payNowUrl;
    window.location.href = url;
  }, [payNowUrl]);

  useEffect(() => {
    if (!contentRef.current || !receiptData) return undefined;
    const apply = () => applyPaymentButtonReadOnlyState(contentRef.current, {
      paymentComplete,
      paidLinkIds,
      paymentAmountCents,
      onPaymentClick: paymentComplete || !paymentEnabled ? null : handlePayClick,
    });
    const cleanup = apply();
    const observer = new MutationObserver(apply);
    observer.observe(contentRef.current, { childList: true, subtree: true });
    return () => {
      cleanup();
      observer.disconnect();
    };
  }, [receiptData, paymentComplete, paymentEnabled, paymentAmountCents, paidLinkIds, handlePayClick]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Loading your signed document...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
          <h1 className="text-lg font-medium text-gray-900 mb-2">Unable to load receipt</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const { documentSnapshot, signatures, fullName, signerEmail, submittedFormAnswers = {} } = receiptData;
  const rawSignatureMeta = receiptData.signatureMeta || {};
  const signatureMeta = Object.fromEntries(
    Object.entries(rawSignatureMeta).map(([blockId, meta]) => [
      blockId,
      { ...meta, signerName: meta.signerName || fullName || '', signerEmail: meta.signerEmail || signerEmail || '' },
    ])
  );
  const title = documentSnapshot?.title || 'Document';
  const editorJson = documentSnapshot?.editorJson != null
    ? (typeof documentSnapshot.editorJson === 'string' ? JSON.parse(documentSnapshot.editorJson) : documentSnapshot.editorJson)
    : null;
  const hidePaymentButtons = !!documentSnapshot?.paymentRevealAfterSignature;

  const handleDownloadPdf = async () => {
    if (!documentSnapshot) return;
    setDownloadingPdf(true);
    try {
      try {
        const sealed = await downloadSealedSignedPdf({ documentId, submissionId, receiptToken });
        if (sealed?.certificate) setSealedCertificate(sealed.certificate);
        toast.success('Sealed PDF downloaded.');
        return;
      } catch (_) {
        // Fall back to a browser copy if the sealed file is not deployed yet.
      }
      let html = documentSnapshot.editorHtml || '';
      if (!html.trim() && editorJson) {
        html = editorJsonToHtml(editorJson);
      }
      const { url, filename } = await generatePdfFromDocument({
        title,
        fileName: documentSnapshot.fileName,
        editorHtml: html,
        textContent: documentSnapshot.textContent || '',
        signatures,
        signatureMeta: signatureMeta || {},
        submittedFormAnswers,
        paymentComplete,
        paidLinkIds,
        paymentAmountCents,
        paymentButtonUrl: paymentComplete ? null : payNowUrl,
        paymentCompletedAt: receiptData.paymentCompletedAt,
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded (browser copy).');
    } catch (err) {
      toast.error(err?.message || 'Failed to generate PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const paidAtDisplay = formatPaymentCompletedDisplay(receiptData.paymentCompletedAt);

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      {hidePaymentButtons && (
        <style>{`[data-payment-button] { display: none !important; }`}</style>
      )}
      <div className="max-w-3xl mx-auto">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 truncate">{title}</h1>
            <p className="text-sm text-gray-400">Signed copy</p>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {downloadingPdf ? 'Generating...' : 'Download signed PDF'}
            </button>
            {sealedCertificate && (
              <button
                type="button"
                onClick={() => {
                  try {
                    downloadCertificateJson(sealedCertificate, title);
                  } catch (err) {
                    toast.error(err?.message || 'Certificate is not available yet.');
                  }
                }}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Download certificate
              </button>
            )}
          </div>
        </div>

        {paymentComplete && paymentEnabled && (
          <div className="mb-4 flex items-center gap-2 rounded-brand-md border border-hairline bg-state-success-soft px-4 py-3 text-state-success">
            <Check className="h-5 w-5 shrink-0" strokeWidth={2.5} />
            <p className="text-sm font-medium">
              Payment received
              {paidAtDisplay ? ` on ${paidAtDisplay}` : ''}
            </p>
          </div>
        )}

        <SignatureReceiptFlow
          editorJson={editorJson}
          signatures={signatures}
          signatureMeta={signatureMeta}
        />

        <div className="bg-white rounded-lg shadow p-6" ref={contentRef}>
          {editorJson ? (
            <div className="prose prose-slate prose-sm max-w-none">
              <ReadEditorContent
                json={editorJson}
                signatures={signatures}
                signatureMeta={signatureMeta}
                formRenderMode="hiddenSubmitted"
                submittedFormAnswers={submittedFormAnswers}
              />
            </div>
          ) : (
            <p className="text-gray-500">Document content is not available.</p>
          )}
        </div>
      </div>
    </div>
  );
}
